import { NOT_A_REAL_ID, BASEJUMP_BI_CONTRACT_ADDRESS, BASEJUMP_CONTRACT_ADDRESS } from '../constants';
import { base, baseSepolia } from 'wagmi/chains';
import { TokenboundClient } from '@tokenbound/sdk';
import { waitForTransactionReceipt } from '@wagmi/core';

const IS_PRODUCTION = (process.env.REACT_APP_IS_PRODUCTION || 'false').toLowerCase() === 'true'

const getMetadata = async (tokenId) => {
  const metadataUrl = `https://4shc66ds38.execute-api.us-east-1.amazonaws.com/metadata/${tokenId}`;
  const response = await fetch(metadataUrl);
  if (!response.ok) {
    return {};
  }
  return await response.json();
};

export default class FigureModel {
  constructor(alchemyNftData, isMock = true, isSideload = false) {
    if (isMock) {
      this.nftData = alchemyNftData;
      this.nftData.fullImage = '/mocks/figures/full_mock.png';
      this.nftData.isReal = false;
      this.nftData.isSideload = isSideload;
      this.nftData.attributes = [];
    } else {
      const glbPath = alchemyNftData.rawMetadata.vrm.split('_').slice(-1)[0].replace('.vrm', '.glb');
      this.nftData = {
        id: alchemyNftData.tokenId,
        attributes: alchemyNftData.rawMetadata.attributes.map(a => ({ name: a.trait_type, value: a.value})),
        image: alchemyNftData.rawMetadata.image,
        name: alchemyNftData.rawMetadata.name,
        vrm: alchemyNftData.rawMetadata.vrm,
        tokenUri: alchemyNftData.tokenUri.raw,
        isReal: false, //true
        isSideload,
        glb: `https://4shc66ds38.execute-api.us-east-1.amazonaws.com/glb/${glbPath}`,
        fullImage: alchemyNftData.rawMetadata.image_full,
        thumb: alchemyNftData.media[0].thumbnail
      }
    }
  }

  async getEquippedItemIds() {
    if (!this.isReal()) {
      return [];
    }
    const response = await fetch(this.nftData.tokenUri);
    const data = await response.json();
    const equippedIds = data.equipment.map((i) => i.token_id);
    return equippedIds;
  }

  isReal() {
    return !!this.nftData.isReal;
  }

  isSideload() {
    return !!this.nftData.isSideload;
  }

  isHenchie() {
    return !!this.nftData.isHenchie;
  }

  static fromAlchemyNft(alchemyNftData, isMock = true, isSideload = false) {
    return new FigureModel(alchemyNftData, isMock, isSideload);
  }

  static async fromOgNft(alchemyNftData) {
    const rawMetadata = await getMetadata(alchemyNftData.id);
    rawMetadata.image = alchemyNftData.image;
    rawMetadata.vrm = alchemyNftData.vrm;
    alchemyNftData.tokenId = alchemyNftData.id;
    alchemyNftData.rawMetadata = rawMetadata;
    alchemyNftData.tokenUri = { raw: '' };
    alchemyNftData.media = [{ thumbnail: alchemyNftData.thumbnail }];
    return new FigureModel(alchemyNftData, false);
  }

  getScaleFactor() {
    return this.nftData.scaleFactor || 1;
  }

  getTitle() {
    return this.nftData.name;
  }

  getId() {
    return this.nftData.id;
  }

  getName() {
    const name = this.nftData.name.split('-').pop();
    return name.trim();
  }

  isOg() {
    return parseInt(this.nftData.id) <= 7290
  }

  getAvatarImgSource() {
    return this.nftData.image;
  }
  
  getAvatarThumbSource() {
    return this.nftData.thumb || this.nftData.image;
  }

  getFullImgSource() {
    return this.nftData.fullImage;
  }

  getGlbSource() {
    return this.nftData.glb;
  }

  getVrmSource() {
    return this.nftData.vrm;
  }

  async sendAssetTo(assetId, receiverFigure, walletClient, wagmiConfig, notifyStep) {
    const TBAccount = {
      tokenContract: BASEJUMP_CONTRACT_ADDRESS,
      tokenId: this.nftData.id
    };
    const tokenboundClient = new TokenboundClient({ walletClient, chainId: IS_PRODUCTION ? base.id : baseSepolia.id });
    const ownAddress = tokenboundClient.getAccount(TBAccount);
    const isAccountDeployed = await tokenboundClient.checkAccountDeployment({
      accountAddress: ownAddress,
    });

    if (!isAccountDeployed) {
      try {
        if (notifyStep) {
          notifyStep('Creating TBA...');
	}
        const { account, txHash } = await tokenboundClient.createAccount({
          tokenContract: BASEJUMP_CONTRACT_ADDRESS,
          tokenId: this.nftData.id
        });
        const transactionReceipt = await waitForTransactionReceipt(wagmiConfig, {
          hash: txHash
        });
      } catch (error) {
        throw new Error('Cannot create tokenbound account!');
      }
    }

    try {
      notifyStep('Transferring...');
      const transferData = {
        account: ownAddress,
        tokenType: 'ERC1155',
        tokenContract: BASEJUMP_BI_CONTRACT_ADDRESS,
        tokenId: `${assetId}`,
        recipientAddress: receiverFigure.get6551Address()
      };
      const txHash = await tokenboundClient.transferNFT(transferData);
      const transactionReceipt = await waitForTransactionReceipt(wagmiConfig, {
        hash: txHash
      });
    } catch (error) {
      throw new Error('Cannot transfer asset!');
    }
  }

  get6551Address(walletClient) {
    if (!this.isReal()) {
      return NOT_A_REAL_ID;
    }

    const TBAccount = {
      tokenContract: BASEJUMP_CONTRACT_ADDRESS,
      tokenId: this.nftData.id
    };
    const tokenboundClient = new TokenboundClient({ walletClient, chainId: IS_PRODUCTION ? base.id : baseSepolia.id });
    const account = tokenboundClient.getAccount(TBAccount);
    // TODO: https://docs.tokenbound.org/sdk/methods#checkaccountdeployment
    // And then TODO: https://docs.tokenbound.org/sdk/methods#createaccount
    return account;
  }

  getOpenSeaLink() {
    return IS_PRODUCTION
      ? `https://opensea.io/assets/base/${BASEJUMP_CONTRACT_ADDRESS}/${this.getId()}`
      : `https://testnets.opensea.io/assets/base-sepolia/${BASEJUMP_CONTRACT_ADDRESS}/${this.getId()}`;
  }

  getTraits() {
    return this.nftData.attributes;
  }
};
